import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { Content } from "../../components/common/ContentBox";
import {
  CompassParagraph,
  TextBox,
} from "../../components/common/styledComponents";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
} from "../../components/common/Modal";
import {
  ElementContainer,
  RowContainer,
  ColumnContainer,
  FlexibleContainer,
} from "../../components/containers/styledComponents";
import { greyText } from "../../styles";
import { NoteEditor, NoteViewer } from "../../components/common/Note";

import flag from "../../assets/svg/flag.svg";
import { MESSAGES } from "../../messages";
import { Note, NoteResponse } from "../../models/workflowModels";
import { usePost } from "../../hooks/usePost";
import { useToast } from "../../store/ToastProvider";
import { Avatar } from "../../components/visuals/Avatar";
import { formatReadableDate } from "../../utils/formattingUtils";
import { resolveDisplayName } from "../../constants";

const TitleDisplay = styled(TextBox)`
  margin-right: 0;

  p {
    font-family: "ApercuMedium";
    font-size: 15px;
  }
`;

export const NoteModal: React.FC<{
  onNoteAdd: (note: Note) => void;
  onHide: () => void;
  shouldDisplay?: boolean;
}> = ({ shouldDisplay, onHide, onNoteAdd }) => {
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const [note, setNote] = useState<Content>({
    title: "",
    content: "",
  });

  const {
    sendData: getNote,
    data: retrievedNotes,
    error: getNoteError,
  } = usePost<NoteResponse[], { note_id: string }>();

  const noteId = searchParams.get("noteId");
  const retrievedNote = retrievedNotes && retrievedNotes[0];
  const isViewModal = !!noteId;

  const noteModalConfig: NoteModalConfig = {
    author: resolveDisplayName(
      retrievedNote?.created_by || MESSAGES.NOT_AVAILABLE_TEXT,
    ),
    addedOn: formatReadableDate(
      retrievedNote?.timestamp || MESSAGES.NOT_AVAILABLE_TEXT,
    ),
    modalTitle: isViewModal ? "View Note" : "Add Note",
    taskId: searchParams.get("taskId") || MESSAGES.NOT_AVAILABLE_TEXT,
    description: isViewModal
      ? MESSAGES.WORKSPACE.NOTE_MODAL.VIEW_NOTE_DESCRIPTION(
          formatReadableDate(
            retrievedNote?.timestamp || MESSAGES.NOT_AVAILABLE_TEXT,
          ),
        )
      : MESSAGES.WORKSPACE.NOTE_MODAL.ADD_NOTE_DESCRIPTION,
  };

  useEffect(() => {
    setNote({
      title: "",
      content: "",
    });
    if (noteId) {
      getNote("GET_NOTE", { note_id: noteId });
    }
  }, [shouldDisplay]);

  useEffect(() => {
    if (retrievedNotes) {
      setNote(() => ({
        title: retrievedNotes[0].title,
        content: retrievedNotes[0].content,
      }));
    }
  }, [retrievedNotes]);

  useEffect(() => {
    getNoteError && addToast("error fetching note", "error");
  }, [getNoteError]);

  const submitNote = (): void => {
    onNoteAdd({ ...note, task_id: noteModalConfig.taskId });
  };

  const headerRight = isViewModal ? (
    <ElementContainer ai="center">
      <Avatar username={noteModalConfig.author} />
      <CompassParagraph ml={10} color={greyText}>
        {noteModalConfig.author}
      </CompassParagraph>
    </ElementContainer>
  ) : undefined;

  const footerContent = (
    <ModalFooter
      onClose={onHide}
      onSubmit={!noteId ? submitNote : undefined}
      hideSubmit={!!noteId}
      showDivider={false}
    />
  );

  return (
    <CompassModal
      show={shouldDisplay ?? false}
      handleClose={onHide}
      size="lg"
      title={noteModalConfig.modalTitle}
      titleIcon={flag}
      headerRight={headerRight}
      footer={footerContent}
    >
      <ModalContent>
        {isViewModal ? (
          <>
            <RowContainer mb={1}>
              <ColumnContainer mt={1}>
                <RowContainer mb={1}>
                  <FlexibleContainer ml={"5px"}>
                    <CompassParagraph bold={true}>Title</CompassParagraph>
                  </FlexibleContainer>
                </RowContainer>
                <RowContainer mb={1} mt={3}>
                  <TitleDisplay>
                    <CompassParagraph>{note.title}</CompassParagraph>
                  </TitleDisplay>
                </RowContainer>
              </ColumnContainer>
            </RowContainer>
            <RowContainer mt={5}>
              <ColumnContainer mt={1}>
                <RowContainer mb={1}>
                  <FlexibleContainer ml={"5px"}>
                    <CompassParagraph bold={true}>Content</CompassParagraph>
                  </FlexibleContainer>
                </RowContainer>
                <RowContainer mb={1} mt={3}>
                  <NoteViewer content={note.content} />
                </RowContainer>
              </ColumnContainer>
            </RowContainer>
          </>
        ) : (
          <NoteEditor note={note} onChange={setNote} />
        )}
      </ModalContent>
    </CompassModal>
  );
};

type NoteModalConfig = {
  author: string;
  addedOn: string;
  modalTitle: "View Note" | "Add Note";
  taskId: string;
  description: string;
};
